import { useState } from "react";
import { Search, SlidersHorizontal, X, Download, RotateCcw, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { VERDICTS, MOTIFS, verdictLabel, motifLabel } from "@/crm/qualification";
import { ANCIENNETE_OPTIONS } from "@/crm/anciennete";

export const STATUSES = ["Nouveau", "À qualifier", "À contacter", "Contacté", "En discussion",
  "Rendez-vous prévu", "Proposition envoyée", "Négociation", "Gagné", "Perdu", "À rappeler"];

const SCORE_CATEGORIES = ["Prioritaire", "Bon", "Moyen", "Faible"];

// Codes INSEE de tranche d'effectif salarié, regroupés en paliers utiles à la
// prospection. La valeur envoyée est la liste de codes → $in côté API.
const EFFECTIF_OPTIONS = [
  { value: "00", label: "0 salarié" },
  { value: "01,02,03", label: "1 à 9 salariés" },
  { value: "11,12", label: "10 à 49 salariés" },
  { value: "21,22", label: "50 à 199 salariés" },
  { value: "31,32,41,42,51,52,53", label: "200 salariés et plus" },
];

// Un seul menu à l'écran, deux paramètres d'API (never_contacted / inactive_days).
const ACTIVITY_OPTIONS = [
  { value: "", label: "Toute activité" },
  { value: "never", label: "Jamais contactées" },
  { value: "30", label: "Sans activité depuis 30 j" },
  { value: "60", label: "Sans activité depuis 60 j" },
  { value: "90", label: "Sans activité depuis 90 j" },
];

const CREATED_OPTIONS = [
  { value: "", label: "Toutes les fiches" },
  { value: "1", label: "Ajoutées aujourd'hui" },
  { value: "7", label: "Ajoutées cette semaine" },
  { value: "30", label: "Ajoutées ce mois" },
];

// « Déjà client » : hors des listes par défaut (le serveur applique la même
// règle, voir crm/clients.py). Le filtre sert à les retrouver, pas à les
// remettre dans la prospection.
const CLIENT_OPTIONS = [
  { value: "", label: "Prospects (hors clients)" },
  { value: "true", label: "Clients seulement" },
  { value: "all", label: "Prospects et clients" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "Toutes les sources" },
  { value: "recherche-entreprises", label: "Campagne (SIRENE)" },
  { value: "google_places", label: "Campagne (Google Places)" },
  { value: "ajout-rapide", label: "Ajout rapide (registre public)" },
  { value: "manual", label: "Saisie manuelle" },
  { value: "import_csv", label: "Import CSV" },
  { value: "missed_call", label: "Appel manqué" },
  { value: "call_disposition", label: "Fiche d'appel" },
];

const ETAT_OPTIONS = [
  { value: "", label: "Actives et cessées" },
  { value: "A", label: "Actives uniquement" },
  { value: "C", label: "Cessées uniquement" },
];

// Valeurs stockées par l'extraction (CATEGORIE_NAMES, crm/extraction.py).
const CATEGORIE_OPTIONS = [
  { value: "", label: "Toutes tailles" },
  { value: "PME", label: "PME" },
  { value: "Entreprise de taille intermédiaire", label: "Entreprise de taille intermédiaire" },
  { value: "Grande entreprise", label: "Grande entreprise" },
];

const EXCLUDED_OPTIONS = [
  { value: "", label: "Masquer les oppositions" },
  { value: "true", label: "Oppositions uniquement" },
  { value: "all", label: "Tout afficher" },
];

// Le menu « Tri : » et les en-têtes de colonnes partagent `filters.sort` :
// chaque tri de colonne a donc son entrée ici, dans les deux sens, sinon le
// menu afficherait une valeur qu'il ne connaît pas.
export const SORT_OPTIONS = [
  { value: "-created_at", label: "Ajout récent" },
  { value: "created_at", label: "Ajout ancien" },
  { value: "-lead_score", label: "Score décroissant" },
  { value: "lead_score", label: "Score croissant" },
  { value: "nom", label: "Nom (A→Z)" },
  { value: "-nom", label: "Nom (Z→A)" },
  { value: "libelle_naf", label: "Activité (A→Z)" },
  { value: "-libelle_naf", label: "Activité (Z→A)" },
  { value: "ville", label: "Ville (A→Z)" },
  { value: "-ville", label: "Ville (Z→A)" },
  { value: "status", label: "Statut (A→Z)" },
  { value: "-status", label: "Statut (Z→A)" },
  { value: "commercial_id", label: "Commercial" },
  { value: "-commercial_id", label: "Commercial (inverse)" },
  { value: "-last_activity_at", label: "Activité récente" },
];

export const EMPTY_FILTERS = {
  departement: "", ville: "", code_postal: "", naf: "",
  status: "", score_category: "", min_score: "", max_score: "",
  commercial_id: "", unassigned: false, tag: "",
  has_phone: false, has_email: false, has_website: false,
  tranche_effectif: "", etat_administratif: "", source: "",
  categorie_entreprise: "", region: "", est_siege: false,
  dirigeant: "", dirigeant_qualite: "", has_dirigeant: false,
  anciennete_max_jours: "", created_days: "", activity: "",
  excluded: "", client: "", campaign_id: "", enriched: "",
  qualification_verdict: "", qualification_motif: "", secteur_reel: "", recheck_due: false,
  sort: "-created_at",
};

export const ENRICHED_OPTIONS = [
  { value: "", label: "Toutes" },
  { value: "never", label: "Jamais enrichies" },
  { value: "done", label: "Déjà enrichies" },
];

/** État initial depuis l'URL : une liste filtrée reste partageable et survit à un F5. */
export function hydrateFilters(sp) {
  const out = { ...EMPTY_FILTERS };
  for (const k of Object.keys(EMPTY_FILTERS)) {
    const v = sp.get(k);
    if (v === null) continue;
    out[k] = typeof EMPTY_FILTERS[k] === "boolean" ? v === "true" : v;
  }
  return out;
}

/** Paramètres envoyés à l'API (et à l'export, qui accepte les mêmes). */
export function filtersToParams(filters, search) {
  const p = {};
  if (search?.trim()) p.search = search.trim();
  for (const [k, v] of Object.entries(filters)) {
    if (k === "activity" || k === "sort") continue;
    if (v === true) p[k] = "true";
    else if (v !== false && v !== "" && v != null) p[k] = v;
  }
  if (filters.activity === "never") p.never_contacted = "true";
  else if (filters.activity) p.inactive_days = filters.activity;
  return p;
}

/** Query string de l'URL : mêmes clés que l'état, pour pouvoir la relire. */
export function filtersToSearchParams(filters, search) {
  const p = {};
  if (search?.trim()) p.search = search.trim();
  for (const [k, v] of Object.entries(filters)) {
    if (v === true) p[k] = "true";
    else if (v !== false && v !== "" && v != null && v !== EMPTY_FILTERS[k]) p[k] = v;
  }
  return p;
}

const labelOf = (options, value) => options.find((o) => o.value === value)?.label || value;

/** Une pastille par filtre actif, avec sa fonction de retrait. */
export function describeFilters(filters, search, commercials, campaigns = [], sectors = []) {
  const chips = [];
  const push = (key, label) => chips.push({ key, label });
  if (search?.trim()) push("search", `« ${search.trim()} »`);
  if (filters.departement) push("departement", `Dép. ${filters.departement}`);
  if (filters.ville) push("ville", filters.ville);
  if (filters.code_postal) push("code_postal", `CP ${filters.code_postal}`);
  if (filters.naf) push("naf", `NAF ${filters.naf}`);
  if (filters.status) push("status", filters.status.split(",").join(" · "));
  if (filters.score_category) push("score_category", `Score ${filters.score_category}`);
  if (filters.min_score) push("min_score", `Score ≥ ${filters.min_score}`);
  if (filters.max_score) push("max_score", `Score ≤ ${filters.max_score}`);
  if (filters.commercial_id) {
    const name = commercials.find((c) => c.id === filters.commercial_id)?.name;
    push("commercial_id", name || "Commercial");
  }
  if (filters.unassigned) push("unassigned", "Non attribuées");
  if (filters.tag) push("tag", `Tag ${filters.tag}`);
  if (filters.has_phone) push("has_phone", "Avec téléphone");
  if (filters.has_email) push("has_email", "Avec email");
  if (filters.has_website) push("has_website", "Avec site web");
  if (filters.tranche_effectif) push("tranche_effectif", labelOf(EFFECTIF_OPTIONS, filters.tranche_effectif));
  if (filters.etat_administratif) push("etat_administratif", labelOf(ETAT_OPTIONS, filters.etat_administratif));
  if (filters.source) push("source", labelOf(SOURCE_OPTIONS, filters.source));
  if (filters.categorie_entreprise) push("categorie_entreprise", filters.categorie_entreprise);
  if (filters.region) push("region", filters.region);
  if (filters.est_siege) push("est_siege", "Sièges sociaux");
  if (filters.dirigeant) push("dirigeant", `Dirigeant : ${filters.dirigeant}`);
  if (filters.dirigeant_qualite) push("dirigeant_qualite", `Fonction : ${filters.dirigeant_qualite}`);
  if (filters.has_dirigeant) push("has_dirigeant", "Avec dirigeant connu");
  if (filters.anciennete_max_jours) {
    const o = ANCIENNETE_OPTIONS.find((x) => String(x.value) === String(filters.anciennete_max_jours));
    push("anciennete_max_jours", o?.short || "Ancienneté");
  }
  if (filters.created_days) push("created_days", labelOf(CREATED_OPTIONS, filters.created_days));
  if (filters.activity) push("activity", labelOf(ACTIVITY_OPTIONS, filters.activity));
  if (filters.excluded) push("excluded", labelOf(EXCLUDED_OPTIONS, filters.excluded));
  if (filters.client) push("client", labelOf(CLIENT_OPTIONS, filters.client));
  if (filters.campaign_id) {
    const name = campaigns.find((c) => c.id === filters.campaign_id)?.name;
    push("campaign_id", name ? `Campagne ${name}` : "Campagne");
  }
  if (filters.enriched) push("enriched", labelOf(ENRICHED_OPTIONS, filters.enriched));
  if (filters.qualification_verdict) {
    push("qualification_verdict", filters.qualification_verdict === "none" ? "Non qualifiées"
      : filters.qualification_verdict.split(",").map(verdictLabel).join(" · "));
  }
  if (filters.qualification_motif) push("qualification_motif", motifLabel(filters.qualification_motif));
  if (filters.secteur_reel) {
    const name = (sectors || []).find((x) => x.slug === filters.secteur_reel)?.domain;
    push("secteur_reel", `Secteur réel ${name || filters.secteur_reel}`);
  }
  if (filters.recheck_due) push("recheck_due", "À revérifier");
  return chips;
}

const FIELD = "w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15";
const LABEL = "block text-xs text-slate-500 mb-1";

const isSet = (key, value) => value !== EMPTY_FILTERS[key] && value !== "" && value !== false;

/** Groupe repliable. Fermé par défaut : on ne déplie que ce qu'on cherche.
 *  Exception : un groupe qui porte déjà un filtre actif s'ouvre, sinon un
 *  critère venu de l'URL resterait invisible. */
function Group({ id, title, keys, filters, children, cols = "sm:grid-cols-2 lg:grid-cols-4" }) {
  const count = keys.filter((k) => isSet(k, filters[k])).length;
  const [open, setOpen] = useState(count > 0);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        data-testid={`filter-group-${id}`} className="w-full flex items-center gap-2 py-3 text-left">
        <ChevronDown size={14} className={cn("shrink-0 text-slate-400 transition-transform", !open && "-rotate-90")} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</span>
        {count > 0 && (
          <span className="h-4 min-w-4 px-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-mono flex items-center justify-center">
            {count}
          </span>
        )}
      </button>
      {open && <div className={cn("grid gap-3 pb-4", cols)}>{children}</div>}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div className="min-w-0">
      <label className={LABEL}>{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={FIELD}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Text({ label, value, onChange, placeholder, testid, mono, type = "text" }) {
  return (
    <div className="min-w-0">
      <label className={LABEL}>{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={cn(FIELD, mono && "font-mono")} />
    </div>
  );
}

function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export default function CompanyFilters({
  filters, onChange, search, onSearch, commercials, campaigns = [], sectors = [], isManager, isAdmin, onExport, onReset,
}) {
  const [open, setOpen] = useState(false);
  const chips = describeFilters(filters, search, commercials, campaigns, sectors);
  // Une campagne archivée reste filtrable : ses entreprises, elles, sont
  // toujours là. On la signale, et on n'en propose pas les brouillons vides.
  const campaignOptions = campaigns
    .filter((c) => c.companies_count > 0 || c.id === filters.campaign_id)
    .map((c) => ({ value: c.id,
                   label: `${c.name}${c.archived ? " · archivée" : ""} (${c.companies_count})` }));
  const set = (key, value) => onChange({ ...filters, [key]: value });

  // Statuts : multi-sélection stockée en « a,b,c » (l'API la traduit en $in).
  const selectedStatuses = filters.status ? filters.status.split(",") : [];
  const toggleStatus = (s) => {
    const next = selectedStatuses.includes(s)
      ? selectedStatuses.filter((x) => x !== s)
      : [...selectedStatuses, s];
    set("status", next.join(","));
  };

  const clearChip = (key) => {
    if (key === "search") return onSearch("");
    onChange({ ...filters, [key]: EMPTY_FILTERS[key] });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-4" data-testid="company-filters">
      {/* Barre toujours visible */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3">
        <div className="flex-1 flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2">
          <Search size={15} className="shrink-0 text-slate-400" />
          <input data-testid="filter-search" placeholder="Nom, SIRET, ville…" value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
          {search && (
            <button onClick={() => onSearch("")} aria-label="Effacer la recherche" className="shrink-0 text-slate-300 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>
        <select data-testid="filter-sort" value={filters.sort} onChange={(e) => set("sort", e.target.value)}
          className={cn(FIELD, "sm:w-48")}>
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>Tri : {o.label}</option>)}
        </select>
        <button data-testid="toggle-advanced-filters" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className={cn("flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm whitespace-nowrap",
            open ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-300 text-slate-700 hover:bg-slate-50")}>
          <SlidersHorizontal size={15} /> Filtres avancés
          {chips.length > 0 && (
            <span data-testid="active-filters-count"
              className="h-5 min-w-5 px-1 rounded-full bg-indigo-600 text-white text-[11px] font-mono flex items-center justify-center">
              {chips.length}
            </span>
          )}
        </button>
        {/* Export réservé à l'admin (le serveur répond 403 aux autres rôles). */}
        {isAdmin && (
          <button onClick={onExport} data-testid="export-btn"
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 whitespace-nowrap">
            <Download size={15} /> Exporter
          </button>
        )}
      </div>

      {/* Pastilles des filtres actifs */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 pb-3" data-testid="filter-chips">
          {chips.map((c) => (
            <span key={c.key} data-testid={`filter-chip-${c.key}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 pl-2.5 pr-1.5 py-1 text-xs text-slate-600">
              {c.label}
              <button onClick={() => clearChip(c.key)} aria-label={`Retirer le filtre ${c.label}`}
                className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
            </span>
          ))}
          <button data-testid="filters-reset" onClick={onReset}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 ml-1">
            <RotateCcw size={12} /> Tout effacer
          </button>
        </div>
      )}

      {/* Panneau avancé */}
      {open && (
        <div className="px-3 pb-2 border-t border-slate-100" data-testid="advanced-filters">
          <Group id="localisation" title="Localisation" keys={["departement", "ville", "code_postal", "naf", "region"]} filters={filters}>
            <Text label="Département" testid="filter-departement" mono placeholder="69 ou 69,38"
              value={filters.departement} onChange={(v) => set("departement", v)} />
            <Text label="Ville" testid="filter-ville" placeholder="Lyon"
              value={filters.ville} onChange={(v) => set("ville", v)} />
            <Text label="Code postal" testid="filter-code-postal" mono placeholder="69003"
              value={filters.code_postal} onChange={(v) => set("code_postal", v)} />
            <Text label="Code NAF (préfixe)" testid="filter-naf" mono placeholder="56 ou 5610"
              value={filters.naf} onChange={(v) => set("naf", v)} />
            <Text label="Région" testid="filter-region" placeholder="Auvergne-Rhône-Alpes"
              value={filters.region} onChange={(v) => set("region", v)} />
          </Group>

          <Group id="qualification" title="Qualification"
            keys={["status", "score_category", "min_score", "max_score", "tag"]} filters={filters}>
            <div className="sm:col-span-2 lg:col-span-4 min-w-0">
              <label className={LABEL}>Statuts (plusieurs possibles)</label>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((st) => {
                  const on = selectedStatuses.includes(st);
                  return (
                    <button key={st} data-testid={`filter-status-${st}`} onClick={() => toggleStatus(st)} aria-pressed={on}
                      className={cn("px-2.5 py-1 rounded-lg text-xs font-medium border",
                        on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")}>
                      {st}
                    </button>
                  );
                })}
              </div>
            </div>
            <Select label="Catégorie de score" testid="filter-score" value={filters.score_category}
              onChange={(v) => set("score_category", v)}
              options={[{ value: "", label: "Toutes catégories" }, ...SCORE_CATEGORIES.map((x) => ({ value: x, label: x }))]} />
            <Text label="Score minimum" testid="filter-minscore" type="number" placeholder="0"
              value={filters.min_score} onChange={(v) => set("min_score", v)} />
            <Text label="Score maximum" testid="filter-maxscore" type="number" placeholder="100"
              value={filters.max_score} onChange={(v) => set("max_score", v)} />
            <Text label="Tag" testid="filter-tag" placeholder="VIP ou VIP,RELANCE"
              value={filters.tag} onChange={(v) => set("tag", v)} />
          </Group>

          <Group id="suivi" title="Suivi commercial"
            keys={["commercial_id", "unassigned", "activity", "created_days", "excluded", "client"]} filters={filters}>
            {isManager && (
              <Select label="Commercial" testid="filter-commercial" value={filters.commercial_id}
                onChange={(v) => set("commercial_id", v)}
                options={[{ value: "", label: "Tous commerciaux" }, ...commercials.map((c) => ({ value: c.id, label: c.name }))]} />
            )}
            <Select label="Activité" testid="filter-activity" value={filters.activity}
              onChange={(v) => set("activity", v)} options={ACTIVITY_OPTIONS} />
            <Select label="Ajout au CRM" testid="filter-created" value={filters.created_days}
              onChange={(v) => set("created_days", v)} options={CREATED_OPTIONS} />
            <Select label="Oppositions" testid="filter-excluded" value={filters.excluded}
              onChange={(v) => set("excluded", v)} options={EXCLUDED_OPTIONS} />
            <Select label="Clients" testid="filter-client" value={filters.client}
              onChange={(v) => set("client", v)} options={CLIENT_OPTIONS} />
            {isManager && (
              <div className="flex items-end pb-2">
                <Check label="Non attribuées" testid="filter-unassigned" checked={filters.unassigned}
                  onChange={(v) => set("unassigned", v)} />
              </div>
            )}
          </Group>

          <Group id="dirigeants" title="Dirigeants"
            keys={["dirigeant", "dirigeant_qualite", "has_dirigeant"]} filters={filters}>
            <Text label="Nom du dirigeant" testid="filter-dirigeant" placeholder="Dupont"
              value={filters.dirigeant} onChange={(v) => set("dirigeant", v)} />
            <Text label="Fonction" testid="filter-dirigeant-qualite" placeholder="Gérant, Président…"
              value={filters.dirigeant_qualite} onChange={(v) => set("dirigeant_qualite", v)} />
            <div className="flex items-end pb-2 sm:col-span-2">
              <Check label="Avec au moins un dirigeant connu" testid="filter-has_dirigeant"
                checked={filters.has_dirigeant} onChange={(v) => set("has_dirigeant", v)} />
            </div>
            <p className="sm:col-span-2 lg:col-span-4 text-xs text-slate-400 text-pretty">
              Nom et fonction désignent la même personne : « Dupont » + « Gérant » ne remonte pas une
              entreprise où Dupont est associé et quelqu'un d'autre est gérant. Les dirigeants viennent
              des données publiques, absentes des fiches saisies à la main.
            </p>
          </Group>

          <Group id="profil" title="Profil de l'entreprise"
            keys={["tranche_effectif", "etat_administratif", "source", "campaign_id", "anciennete_max_jours", "categorie_entreprise", "est_siege"]}
            filters={filters}>
            <Select label="Campagne d'extraction" testid="filter-campaign" value={filters.campaign_id}
              onChange={(v) => set("campaign_id", v)}
              options={[{ value: "", label: "Toutes campagnes" }, ...campaignOptions]} />
            <Select label="Tranche d'effectif" testid="filter-effectif" value={filters.tranche_effectif}
              onChange={(v) => set("tranche_effectif", v)}
              options={[{ value: "", label: "Tous effectifs" }, ...EFFECTIF_OPTIONS]} />
            <Select label="Taille d'entreprise" testid="filter-categorie" value={filters.categorie_entreprise}
              onChange={(v) => set("categorie_entreprise", v)} options={CATEGORIE_OPTIONS} />
            <Select label="État administratif" testid="filter-etat" value={filters.etat_administratif}
              onChange={(v) => set("etat_administratif", v)} options={ETAT_OPTIONS} />
            <Select label="Source" testid="filter-source" value={filters.source}
              onChange={(v) => set("source", v)} options={SOURCE_OPTIONS} />
            {/* Mêmes paliers qu'à la création de campagne (source unique :
                crm/anciennete.js), pour que les deux écrans parlent le même langage. */}
            <Select label="Ancienneté de l'entreprise" testid="filter-anciennete"
              value={filters.anciennete_max_jours} onChange={(v) => set("anciennete_max_jours", v)}
              options={ANCIENNETE_OPTIONS.map((o) => ({ value: String(o.value), label: o.short }))} />
            <div className="flex items-end pb-2">
              <Check label="Sièges sociaux uniquement" testid="filter-est_siege"
                checked={filters.est_siege} onChange={(v) => set("est_siege", v)} />
            </div>
          </Group>

          <Group id="qualification" title="Qualification"
            keys={["qualification_verdict", "qualification_motif", "secteur_reel", "recheck_due"]} filters={filters}>
            <Select label="Verdict" testid="filter-qualification" value={filters.qualification_verdict}
              onChange={(v) => set("qualification_verdict", v)}
              options={[{ value: "", label: "Toutes" }, { value: "none", label: "Non qualifiées" }, ...VERDICTS,
                        { value: "hors_cible,a_verifier", label: "Hors cible ou à vérifier" }]} />
            <Select label="Motif" testid="filter-qualification-motif" value={filters.qualification_motif}
              onChange={(v) => set("qualification_motif", v)}
              options={[{ value: "", label: "Tous motifs" }, ...MOTIFS]} />
            <Select label="Secteur réel constaté" testid="filter-secteur-reel" value={filters.secteur_reel}
              onChange={(v) => set("secteur_reel", v)}
              options={[{ value: "", label: "Tous" }, ...(sectors || []).map((x) => ({ value: x.slug, label: x.domain }))]} />
            <div className="flex items-end pb-2">
              <Check label="À revérifier (échéance passée)" testid="filter-recheck_due" checked={filters.recheck_due}
                onChange={(v) => set("recheck_due", v)} />
            </div>
          </Group>

          <Group id="donnees" title="Données présentes" cols="grid-cols-2 sm:grid-cols-4"
            keys={["has_phone", "has_email", "has_website", "enriched"]} filters={filters}>
            <Check label="Téléphone" testid="filter-has_phone" checked={filters.has_phone} onChange={(v) => set("has_phone", v)} />
            <Check label="Email" testid="filter-has_email" checked={filters.has_email} onChange={(v) => set("has_email", v)} />
            <Check label="Site web" testid="filter-has_website" checked={filters.has_website} onChange={(v) => set("has_website", v)} />
            <Select label="Enrichissement" testid="filter-enriched" value={filters.enriched}
              onChange={(v) => set("enriched", v)} options={ENRICHED_OPTIONS} />
          </Group>
        </div>
      )}
    </div>
  );
}
